import { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { hasAnyRole } from '../auth/roles'
import { useBranding } from '../brand/BrandingProvider'
import { Mark } from '../brand/Mark'
import { drainLegacyOutbox } from '../data/legacyOutbox'
import { isAllowedOrigin } from '../runtimeConfig'
import { useBrand } from '../theme/ThemeProvider'
import styles from './AppShell.module.css'
import { Icon } from './icons'
import { SECTIONS, sectionOf, UTILITY_ITEMS, visibleItems } from './nav'
import { RanchSwitcher } from './RanchSwitcher'
import { WrongAddress } from './WrongAddress'

/*
  The shell (owner, 2026-10-23: direction B, "Ranch office"). A dark top bar holds the mark,
  the five sections (Home, Herd, Land, Work, Books), the ranch switcher, Settings and the
  person; a light row under it lists the open section's pages; the page sits in a centred
  column. The top bar is painted with the nav tokens, so a ranch theme reaches it as it
  reached the sidebar (brief §2.8).
*/
export function AppShell() {
  const auth = useAuth()
  const branding = useBranding()
  const brand = useBrand()
  const { pathname } = useLocation()
  const section = sectionOf(pathname)
  const signedIn = Boolean(auth.me)
  const admin = hasAnyRole(auth.roles, 'platform-admin')

  // Records the retired offline app still held in this browser go up once signed in (ADR 0020).
  useEffect(() => {
    if (signedIn) void drainLegacyOutbox()
  }, [signedIn])

  if (!isAllowedOrigin()) {
    return <WrongAddress />
  }

  const initials = (auth.me?.user.displayName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')

  return (
    <div className={`app-shell ${styles.shell}`}>
      <header className={styles.top}>
        <div className={styles.topInner}>
          <Link to="/" className={styles.brand}>
            {brand.logoDarkUrl ? (
              <img src={brand.logoDarkUrl} alt="" className={styles.brandLogo} />
            ) : (
              <Mark size={26} strokeWidth={14} />
            )}
            <span className={styles.brandName}>
              {brand.themed ? brand.ranchName : branding.productName}
            </span>
          </Link>

          <nav aria-label="Main" className={styles.sections}>
            {SECTIONS.map((s) => (
              <Link
                key={s.key}
                to={s.to}
                className={styles.sectionLink}
                aria-current={s.key === section.key ? 'page' : undefined}
              >
                {s.label}
              </Link>
            ))}
          </nav>

          <span className={styles.spacer} />

          <div className={styles.controls}>
            {auth.memberships.length > 1 ? (
              <RanchSwitcher />
            ) : (
              !brand.themed &&
              brand.ranchName && <span className={styles.ranchName}>{brand.ranchName}</span>
            )}
            {visibleItems(UTILITY_ITEMS, admin).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={styles.iconLink}
                aria-label={item.label}
                title={item.label}
              >
                <Icon name={item.icon} />
              </NavLink>
            ))}
            <div className={styles.user}>
              {auth.me && (
                <span className={styles.avatar} aria-hidden="true" title={auth.me.user.displayName}>
                  {initials}
                </span>
              )}
              {auth.me && <span className="visuallyHidden">{auth.me.user.displayName}</span>}
              <button
                type="button"
                className={styles.signOut}
                onClick={auth.signOut}
                disabled={!auth.canSignOut}
                title={auth.canSignOut ? 'End the session' : 'No session to end'}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {section.items.length > 0 && (
        <div className={styles.sub}>
          <nav aria-label={`${section.label} pages`} className={styles.subInner}>
            {section.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={styles.subLink}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      <main className={styles.main}>
        {auth.loading && <p className="text-muted">Signing you in…</p>}
        {auth.error && !auth.me && (
          <div className={`status-danger ${styles.notice}`} role="alert">
            <Icon name="failed" />
            <span>
              The API refused the sign-in: {auth.error.message}
              {auth.correlationId && ` (ref ${auth.correlationId})`}
            </span>
          </div>
        )}
        {auth.me && <Outlet />}
      </main>

      {brand.themed && (
        <footer className={styles.footer}>
          <Mark size={14} strokeWidth={18} />
          <span>Powered by {branding.productName}</span>
        </footer>
      )}
    </div>
  )
}
